import type { Editor } from "@tiptap/react"
import { useCurrentEditor, useEditorState } from "@tiptap/react"

/**
 * Hook that provides access to a Tiptap editor instance.
 *
 * Accepts an optional editor instance directly, or falls back to retrieving
 * the editor from the Tiptap context if available. This allows components
 * to work both when given an editor directly and when used within a Tiptap
 * editor context.
 *
 * Returning the editor does not subscribe to its transactions, so callers
 * re-render only on their own state and event handlers, never per keystroke.
 * Components that read editor state during render and must stay current while
 * mounted call useRerenderOnTransaction.
 *
 * @param providedEditor - Optional editor instance to use instead of the context editor
 * @returns The provided editor or the editor from context, whichever is available
 */
export function useTiptapEditor(providedEditor?: Editor | null): {
  editor: Editor | null
} {
  const { editor: coreEditor } = useCurrentEditor()
  return { editor: providedEditor || coreEditor }
}

/**
 * Re-renders the calling component on every editor transaction. Reserve it for
 * short-lived UI such as an open menu, since each keystroke pays for the render.
 */
export function useRerenderOnTransaction(editor: Editor | null | undefined) {
  useEditorState({
    editor: editor ?? null,
    selector: context => context.transactionNumber,
  })
}
